import { router } from "@inertiajs/react"
import { useState } from "react"
import { Award, Check, Download, Eye, RotateCcw, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from "@/components/ui/responsive-dialog"
import { cn } from "@/lib/utils"

// Ascend slice 5 — who is certified, shown to coaches and admins, and an
// admin's view of quiz progress with the reset action (plan decision 9).
// The server sends empty lists to a non-beta viewer, so these render nothing.

export function CertificationsCard({ certifications = [] }) {
  if (certifications.length === 0) return null

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Award className="h-4 w-4 text-green-600" />
          Certifications
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {certifications.map((cert) => (
          <div
            key={cert.id}
            className="flex flex-wrap items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3"
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-zinc-900">{cert.trackTitle}</p>
              <p className="text-xs text-zinc-600">
                Earned {cert.earnedOn}
                {cert.scorePercent != null && ` · final score ${cert.scorePercent}%`} · No. {cert.certificateNumber}
              </p>
            </div>
            <Button asChild variant="outline" size="sm" className="min-h-[44px]">
              <a href={cert.pdfUrl} target="_blank" rel="noopener noreferrer">
                <Download className="h-4 w-4" />
                Certificate
              </a>
            </Button>
          </div>
        ))}
      </CardContent>
    </Card>
  )
}

function attemptsLabel(row) {
  return row.maxAttempts ? `${row.attemptsUsed} of ${row.maxAttempts}` : `${row.attemptsUsed} (no limit)`
}

// Every attempt at one quiz, in full, for an admin or coach: each question as
// the trainee saw it, their picks, the correct answer, their written answers.
function AttemptAnswers({ attempt }) {
  return (
    <div className="space-y-3">
      {attempt.answers.map((row, i) => (
        <div key={`${row.questionId}-${i}`} className="rounded-lg border border-zinc-200 p-3 space-y-2">
          <p className="text-sm font-medium text-zinc-900">
            <span className="font-mono text-xs text-kf-red mr-2">Q{i + 1}.</span>
            {row.text}
          </p>
          {row.questionType === "free_text" ? (
            <p className="rounded bg-zinc-50 px-3 py-2 text-sm text-zinc-700 whitespace-pre-wrap">
              {row.responseText || <span className="italic text-zinc-400">No answer</span>}
            </p>
          ) : (
            <ul className="space-y-1">
              {row.choices.map((c) => {
                const picked = row.selectedKeys.includes(c.key)
                const right = row.correctKeys.includes(c.key)
                return (
                  <li
                    key={c.key}
                    className={cn(
                      "flex items-center gap-2 rounded px-2 py-1 text-sm",
                      right && "bg-green-50 text-green-900",
                      picked && !right && "bg-red-50 text-red-900"
                    )}
                  >
                    {right ? (
                      <Check className="h-3.5 w-3.5 text-green-600 shrink-0" />
                    ) : picked ? (
                      <X className="h-3.5 w-3.5 text-red-600 shrink-0" />
                    ) : (
                      <span className="h-3.5 w-3.5 shrink-0" />
                    )}
                    <span className="flex-1">{c.text}</span>
                    {picked && <span className="text-[11px] font-medium uppercase tracking-wide">Picked</span>}
                  </li>
                )
              })}
              {row.selectedKeys.length === 0 && <li className="px-2 text-xs italic text-zinc-400">No answer picked</li>}
            </ul>
          )}
          {row.rationale && row.questionType !== "free_text" && <p className="text-xs text-zinc-500">{row.rationale}</p>}
        </div>
      ))}
    </div>
  )
}

function QuizAnswersDialog({ answers, open, onOpenChange }) {
  const [shown, setShown] = useState(0)
  if (!answers) return null
  const attempt = answers.attempts[shown] || answers.attempts[0]
  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle className="font-display text-xl">{answers.lessonTitle}</ResponsiveDialogTitle>
          <ResponsiveDialogDescription>{answers.courseTitle}</ResponsiveDialogDescription>
        </ResponsiveDialogHeader>
        <div className="space-y-4 px-4 pb-4 sm:px-0">
          <div className="flex flex-wrap gap-2">
            {answers.attempts.map((a, i) => (
              <button
                key={a.attemptNumber}
                type="button"
                onClick={() => setShown(i)}
                className={cn(
                  "rounded-full border px-3 py-1.5 text-xs min-h-[36px]",
                  i === shown ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 text-zinc-600 hover:border-zinc-400"
                )}
              >
                Attempt {a.attemptNumber} · {a.scorePercent}% {a.passed ? "passed" : "not passed"}
                {a.reset && " · reset"}
              </button>
            ))}
          </div>
          {attempt && <AttemptAnswers attempt={attempt} />}
        </div>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}

export function QuizProgressCard({ quizProgress = [], accountId, quizAnswers = null }) {
  const [resetting, setResetting] = useState(null)
  const [answersOpen, setAnswersOpen] = useState(false)
  const [loading, setLoading] = useState(null)
  if (quizProgress.length === 0) return null

  // The answers load only when asked for (a partial reload of `quizAnswers`).
  const viewAnswers = (row) => {
    setLoading(row.lessonId)
    router.reload({
      only: ["quizAnswers"],
      data: { quiz_lesson: row.lessonId },
      preserveScroll: true,
      onSuccess: () => setAnswersOpen(true),
      onFinish: () => setLoading(null),
    })
  }

  const reset = (row) => {
    setResetting(row.lessonId)
    // preserveState keeps any unsaved edits elsewhere on the profile page.
    router.post(row.resetUrl, { account_id: accountId }, {
      preserveScroll: true,
      preserveState: true,
      onFinish: () => setResetting(null),
    })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Quizzes</CardTitle>
      </CardHeader>
      <CardContent className="divide-y divide-zinc-100 p-0">
        {quizProgress.map((row) => (
          <div key={row.lessonId} className="flex flex-wrap items-center gap-3 px-6 py-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-zinc-900 truncate">{row.lessonTitle}</p>
              <p className="text-xs text-zinc-500 truncate">{row.courseTitle}</p>
            </div>
            <div className="text-xs text-zinc-600 tabular-nums">
              Attempts {attemptsLabel(row)}
              {row.lastScore != null && ` · last ${row.lastScore}%`}
            </div>
            <span
              className={
                row.passed
                  ? "rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800"
                  : "rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-800"
              }
            >
              {row.passed ? "Passed" : "Not passed"}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="min-h-[44px]"
              disabled={loading === row.lessonId}
              onClick={() => viewAnswers(row)}
            >
              <Eye className="h-3.5 w-3.5" />
              {loading === row.lessonId ? "Loading..." : "View answers"}
            </Button>
            {row.resetUrl && (
              <Button
                variant="outline"
                size="sm"
                className="min-h-[44px]"
                disabled={resetting === row.lessonId}
                onClick={() => reset(row)}
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Reset attempts
              </Button>
            )}
          </div>
        ))}
      </CardContent>
      <QuizAnswersDialog
        key={quizAnswers?.lessonId}
        answers={quizAnswers}
        open={answersOpen && !!quizAnswers}
        onOpenChange={setAnswersOpen}
      />
    </Card>
  )
}
